import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AdmissionCard } from '@components/admission-card';
import {
  ADMISSION_CARD_TAG_ACCESS_CODE,
  ADMISSION_CARD_TAG_IMPORT_ONLY,
  AdmissionCardTag,
} from '@components/admission-card/src/admission-card.type';
import { ModalStickyHeader } from '@components/modals/modal-sticky-header';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { BottleServicePreset, PRESET_TYPE_BOTTLE_SERVICE } from '@domain/preset';
import { getAdmissionDDPPrice } from '@utils/get-admission-ddp-price/get-admission-ddp-price';
import { getAdmissionOnlinePrice } from '@utils/get-admission-online-price/get-admission-online-price';
import { getDefaultOfferNameTranslation } from '@utils/get-default-offer-name-translation/get-default-offer-name-translation';
import { getTranslatedPropertyBasedOnOrder } from '@utils/get-translated-property-based-on-order/get-translated-property-based-on-order';

import { Props } from './select-offer-preset-modal.type';

export const SelectOfferPresetModal = ({
  handleClose,
  floorplanAdmissionsId,
  admissions,
  addPresetBottleService,
}: Props) => {
  const { t, i18n } = useTranslation();
  const { clubState } = useContext(CompanyClubContext);
  const bottleServices = admissions.bottleService.filter(
    (bottleService) => !floorplanAdmissionsId.includes(bottleService.id)
  );

  const handleAdmissionClick = (bottleService: BottleServicePreset) => {
    addPresetBottleService(bottleService);
    handleClose();
  };

  return (
    <>
      <ModalStickyHeader title={t('floorplan:assignTableService')} handleClose={handleClose} bordered={true} />

      <div className='flex flex-col justify-center items-center px-4 md:px-10 py-6'>
        <div className='pt-2 text-secondary text-body-s'>{t('floorplan:orSelectAnExistingFoorPlan')}</div>

        <div className='mt-5 space-y-3 w-full'>
          {bottleServices.map((bottleService) => {
            const defaultOfferNameTranslation = getDefaultOfferNameTranslation(PRESET_TYPE_BOTTLE_SERVICE);
            const onlinePrice = getAdmissionOnlinePrice(bottleService);
            const ddpPrice = getAdmissionDDPPrice(bottleService);
            const offerName =
              getTranslatedPropertyBasedOnOrder({
                translations: bottleService.name,
                preferredLanguage: i18n.language,
              }) ?? t(defaultOfferNameTranslation);

            const offerDescription = getTranslatedPropertyBasedOnOrder({
              translations: bottleService.description,
              preferredLanguage: i18n.language,
            });
            let tags: AdmissionCardTag[] = bottleService.accessCode ? [ADMISSION_CARD_TAG_ACCESS_CODE] : [];

            if (bottleService.settings.isPrivate) tags = [...tags, ADMISSION_CARD_TAG_IMPORT_ONLY];

            return (
              <AdmissionCard
                id={bottleService.id}
                timezone={clubState.city.timezone}
                color={bottleService.color ?? ''}
                name={offerName ?? ''}
                description={offerDescription ?? ''}
                quantity={bottleService.quantity}
                soldItems={0}
                currency={bottleService.price.currency}
                amount={bottleService.price.amount}
                onlinePrice={onlinePrice}
                ddpPrice={ddpPrice}
                isPrivate={bottleService.settings.isPrivate}
                needsUserVerified={false}
                isPublished={false}
                isPreset={true}
                salesEnds={0}
                tags={tags}
                showSubmenuIcon={false}
                onClick={() => handleAdmissionClick(bottleService)}
                guestCapacity={bottleService.guestCapacity}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};
